import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import { DeepLinkServices } from './DeepLinkServices';

const DeepLinkList = () => {
  const navigate = useNavigate();
  const menuDispatch = useDispatch();
  const [deepUrls, setDeepUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');

  const fetchDeepUrls = async () => {
    setLoading(true);
    try {
      const response = await DeepLinkServices.getDeepUrls();
      if (response.success === 1) {
        setDeepUrls(response.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    menuDispatch({ type: 'menuSelect', payload: 'generate-deep-link' });
  }, [menuDispatch]);

  useEffect(() => {
    fetchDeepUrls();
  }, []);

  const visibleRows = deepUrls.filter((item) => {
    const searchText = searchInput.trim().toLowerCase();
    if (!searchText) return true;
    return [item.full_url, item.createdByName]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(searchText));
  });

  const columns = [
    {
      field: 'full_url',
      headerName: 'Full URL',
      flex: 1,
      minWidth: 360,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.7rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'createdByName',
      headerName: 'Created By',
      width: 180,
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      width: 190,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.65rem' }}>
          {params.value ? new Date(params.value).toLocaleString() : ''}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <PageHeaderLayout headerTitle="Show List">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddLinkIcon />}
          onClick={() => navigate('/marketing/generate-deep-link/create')}
        >
          Generate Deep Link
        </Button>
      </PageHeaderLayout>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ width: '100%', backgroundColor: '#fff', borderRadius: '.5rem' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
            <TextField
              size="small"
              placeholder="Search deep links..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: '1rem', mr: 1, color: '#777' }} /> }}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
          </Box>
          <DataGrid
            rows={visibleRows}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            pageSizeOptions={[5, 10, 20, 50]}
            disableColumnMenu
            disableRowSelectionOnClick
            autoHeight
            getRowHeight={() => 'auto'}
            slots={{
              loadingOverlay: () => <CircularProgress size={24} sx={{ mt: 4 }} />,
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                border: 1,
                borderRight: 0,
                borderTop: 0,
                borderColor: 'lightgrey',
                display: 'flex',
                alignItems: 'center',
                py: 1,
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '2px solid rgba(224, 224, 224, 1)',
                borderTop: '2px solid rgba(224, 224, 224, 1)',
                fontWeight: 600,
                fontSize: '0.75rem',
                minHeight: '2rem',
              },
              '& .MuiDataGrid-columnHeader': {
                borderLeft: '1px solid rgba(224, 224, 224, 1)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: '#3e3e3e',
              },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default DeepLinkList;
